import { Component } from '@angular/core';
import { SupportService } from '../../../services/support.service';
import { CompanyService } from '../../../services/company.service';
import { UntypedFormBuilder, Validators, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { SortByValuePipe } from '../../../pipes/sort-by-value.pipe';
import { ServicegroupService } from '../../../services/servicegroup.service';
import { UserService } from '../../../services/user.service';
import { IUser, EAccessLevel, IPUTServiceUser, IPOSTServiceUser, IServiceGroup } from '../../../interfaces/user';
import { map } from 'rxjs/operators';
import { EArchiveState } from '../../../interfaces/archiveState';
import { Observable } from 'rxjs';
import { ButtonComponent, InputFieldComponent, ModalService } from 'shared';
import { TranslateService } from '@ngx-translate/core';
import { AsyncPipe } from '@angular/common';
import { NgSelectComponent } from '@ng-select/ng-select';
import { FilterCompanyOnNamePipe } from '../../../pipes/filter-company-on-name.pipe';
import { SortByNamePipe } from '../../../pipes/sort-company-by-name.pipe';

enum UIState {
  list = 'list',
  create = 'create',
  edit = 'edit',
  modify = 'modify',
}

@Component({
    selector: 'app-developer-support-page',
    templateUrl: './developer-support-page.component.html',
    styleUrls: ['./developer-support-page.component.scss'],
    imports: [
    ButtonComponent,
    InputFieldComponent,
    FormsModule,
    ReactiveFormsModule,
    NgSelectComponent,
    AsyncPipe,
    FilterCompanyOnNamePipe,
    SortByNamePipe
],
})
export class DeveloperSupportPageComponent {
  error;
  uiState = UIState.edit;

  companies$;
  users$;
  serviceGroups$: Observable<IServiceGroup[]>;
  form;

  currentfilter;

  selectedUserId: string = null;

  constructor(
    private readonly supportService: SupportService,
    public userService: UserService,
    private readonly companyService: CompanyService,
    private readonly formBuilder: UntypedFormBuilder,
    private readonly sortByValuePipe: SortByValuePipe,
    private readonly modalService: ModalService,
    private readonly translate: TranslateService,
    private readonly serviceGroupService: ServicegroupService,
  ) {
    this.companies$ = companyService.companies$;
    this.users$ = userService.usersWithCompany$.pipe(
      map((users) =>
        users.filter(
          (user) => user.accessLevel === EAccessLevel.GrundfosClaimAccessServiceGroup && user.archiveState === EArchiveState.Active,
        ),
      ),
    );
    this.serviceGroups$ = serviceGroupService.serviceGroupsChanged$;

    this.form = formBuilder.group({
      Name: [''],
      CompanyId: ['', Validators.required],
      Phone: [''],
      Email: ['', [Validators.required, Validators.email]],
      ServiceGroups: [[], Validators.required],
    });
  }

  createTrainingData() {
    this.error = 'Processing';
    this.supportService.createTrainingSet().then(
      () => {
        this.error = 'Success';
      },
      (error) => {
        this.error = JSON.stringify(error);
      },
    );
  }

  deleteTrainingData() {
    this.error = 'Processing';
    this.supportService.deleteTrainingSets().then(
      () => {
        this.error = 'Success';
      },
      (error) => {
        this.error = JSON.stringify(error);
      },
    );
  }

  private getFormAsUser(): IPOSTServiceUser {
    return {
      name: this.form.get('Name').value,
      companyId: this.form.get('CompanyId').value,
      phone: this.form.get('Phone').value,
      email: this.form.get('Email').value,
      serviceGroupIds: this.form.get('ServiceGroups').value.map((serviceGroup) => serviceGroup.id),
    };
  }

  onSubmit() {
    if (this.selectedUserId === null) {
      // new user
      this.userService.createServiceUser(this.getFormAsUser());
    } else {
      // editing existing user
      const user = this.getFormAsUser() as IPUTServiceUser;
      user.id = this.selectedUserId;
      this.userService.updateServiceUser(user);
    }

    this.uiState = UIState.edit;
  }

  edit() {
    if (this.uiState === UIState.edit) {
      this.uiState = UIState.list;
    } else {
      this.uiState = UIState.edit;
    }
  }

  createNewUser() {
    this.uiState = UIState.create;
    this.selectedUserId = null;
    this.form.patchValue({
      Name: '',
      CompanyId: null,
      Phone: '',
      Email: '',
      ServiceGroups: '',
    });
  }

  deleteUser(user: IUser) {
    this.modalService
      .showConfirmModal(this.translate.instant('archive-page.remove-users'), this.translate.instant('archive-page.confirm-remove-users'))
      .subscribe((confirmed) => {
        if (confirmed) {
          this.userService.archiveUser(user.id).subscribe(() => {});
        }
      });
  }

  modifyUser(user: IUser) {
    this.uiState = UIState.modify;
    this.selectedUserId = user.id;

    this.form.patchValue({
      Name: user.name,
      Phone: user.phone,
      Email: user.email,
      CompanyId: user.companyId,
      ServiceGroups: user.serviceGroups,
    });
  }

  cancelNewUser() {
    this.uiState = UIState.edit;
  }
}
